// crewAssignmentSuggestions.js
import moment from 'moment';
import { isEvenWeek, findNextAppropriateDate } from '../utils/dateUtils';

/**
 * Suggests the best crew and date combinations for an unassigned job
 * @param {Object} unassignedJob - The job that needs to be assigned
 * @param {Array} crews - Available crews
 * @param {Array} weeklyRoutes - Current weekly routes data
 * @param {Object} weeklyHours - Weekly hours data for crews
 * @param {Array} sites - All job sites for proximity calculations
 * @param {Date} startDate - Date to start looking for assignments (default: today)
 * @param {number} daysToLookAhead - Number of days to consider (default: 14)
 * @returns {Array} Array of suggestions sorted by score (best first)
 */
export const suggestCrewAssignments = (
  unassignedJob,
  crews,
  weeklyRoutes,
  weeklyHours,
  sites,
  startDate = new Date(),
  daysToLookAhead = 14,
  includeWeekends = false // New parameter for flexibility
) => {
  const suggestions = [];
  console.log('Making suggestions...', startDate, crews, weeklyHours, weeklyRoutes, sites)
  
  // Generate date range to consider
  const dateRange = [];
  for (let i = 0; i < daysToLookAhead; i++) {
    const date = moment(startDate).add(i, 'days');
    // Optionally include weekends
    if (!includeWeekends && (date.day() === 0 || date.day() === 6)) continue;
    dateRange.push(date.toDate());
  }
  
  console.log(`Evaluating ${dateRange.length} days for ${crews.length} crews = ${dateRange.length * crews.length} total combinations`);
  
  // Calculate suggestions for each crew and date combination
  crews.forEach(crew => {
    dateRange.forEach(date => {
      const suggestion = calculateAssignmentScore(
        unassignedJob,
        crew,
        date,
        weeklyRoutes,
        weeklyHours,
        sites
      );
      
      // Include all suggestions with any positive score
      if (suggestion.score > 0) {
        suggestions.push(suggestion);
      }
    });
  });
  
  // Sort by score descending (best first)
  return suggestions.sort((a, b) => b.score - a.score);
};

/**
 * Calculates a score for assigning a job to a specific crew on a specific date
 * Higher score = better assignment
 */
const calculateAssignmentScore = (job, crew, date, weeklyRoutes, weeklyHours, sites) => {
  let score = 0;
  const factors = {};
  const dayOfWeek = moment(date).format('dddd');
  const weekOfYear = moment(date).week();
  const isEvenWeekWeek = weekOfYear % 2 === 0;
  
  console.log('Crew: %s  Date: %s   Day: %s  isEvenWeekWeek: %s', crew?.name, date, dayOfWeek, isEvenWeekWeek)
  console.log('weeklyHours object:', weeklyHours)
  console.log('Crew hours data:', weeklyHours[crew._id])
  
  // Factor 1: Frequency Compatibility (MEDIUM IMPORTANCE - reduced from HIGH)
  const frequencyScore = calculateFrequencyScore(job, date);
  factors.frequencyScore = frequencyScore;
  score += frequencyScore * 15; // Reduced weight to allow more flexibility
  
  // Factor 2: Current Workload (HIGH IMPORTANCE)
  const workloadScore = calculateWorkloadScore(crew, date, weeklyHours, job.estimatedHours);
  factors.workloadScore = workloadScore;
  score += workloadScore * 20; // High weight
  
  // Factor 3: Geographic Proximity (MEDIUM IMPORTANCE)
  const proximityScore = calculateProximityScore(job, crew, date, weeklyRoutes, sites);
  factors.proximityScore = proximityScore;
  score += proximityScore * 15; // Medium weight
  
  // Factor 4: Route Efficiency (MEDIUM IMPORTANCE)
  const efficiencyScore = calculateRouteEfficiencyScore(job, crew, date, weeklyRoutes);
  factors.efficiencyScore = efficiencyScore;
  score += efficiencyScore * 12; // Medium weight
  
  // Factor 5: Crew Specialization/Experience (LOW IMPORTANCE)
  const specializationScore = 0 // calculateSpecializationScore(job, crew);
  factors.specializationScore = 0 // specializationScore;
  // score += specializationScore * 8; // Lower weight
  
  // Factor 6: Week Balance (for recurring jobs)
  const weekBalanceScore = calculateWeekBalanceScore(job, crew, date, weeklyHours);
  factors.weekBalanceScore = weekBalanceScore;
  score += weekBalanceScore * 10; // Medium weight
  
  // Factor 7: Overtime avoidance
  const overtimeScore = calculateOvertimeScore(crew, date, weeklyHours, job.estimatedHours);
  factors.overtimeScore = overtimeScore;
  score += overtimeScore * 18; // High weight
  
  console.log('Crew: %s  Date: %s   Day: %s   score: %d', crew?.name, date, dayOfWeek, score)
  console.log('frequencyScore: %d workloadScore: %d Proximity: %d  efficiencyScore: %d  specializationScore: %d  weekBalanceScore: %d   overtimeScore: %d', frequencyScore, workloadScore, proximityScore, efficiencyScore, specializationScore, weekBalanceScore, overtimeScore)
  
  return {
    crew,
    date,
    dayOfWeek,
    score: Math.round(score),
    factors,
    estimatedTotalHours: calculateEstimatedDayTotal(crew, date, weeklyHours, job.estimatedHours),
    currentHours: getCurrentDayHours(crew, date, weeklyHours),
    hasAnyRoutes: !!weeklyHours[crew._id],
    hasRoutesToday: !!(weeklyHours[crew._id]?.[dayOfWeek]?.serviceTime || weeklyHours[crew._id]?.[dayOfWeek]?.driveTime),
    reasoning: generateReasoningText(factors, crew, date, job)
  };
};

/**
 * Calculate how well the job frequency matches the proposed date
 * Now with flexibility - gives partial scores for non-ideal days
 */
const calculateFrequencyScore = (job, date) => {
  const dayOfWeek = moment(date).format('dddd');
  const weekOfYear = moment(date).week();
  const isEvenWeekWeek = weekOfYear % 2 === 0;
  
  switch (job.frequency) {
    case 'Daily':
      return 100; // Any weekday works perfectly
      
    case 'Weekly':
      if (dayOfWeek === job.preferredDay) return 100; // Perfect match
      // Give partial credit for other days - flexibility is good!
      return 40; // Can be scheduled on non-preferred day if needed
      
    case 'Bi-Weekly':
      const targetEvenWeek = job.evenOrOddWeeks?.toLowerCase() === 'even';
      const dayMatch = dayOfWeek === job.preferredDay;
      const weekMatch = isEvenWeekWeek === targetEvenWeek;
      
      if (dayMatch && weekMatch) return 100; // Perfect match
      if (weekMatch) return 50; // Right week, wrong day - still workable
      if (dayMatch) return 30; // Right day, wrong week - less ideal
      return 20; // Wrong day and week - but still possible
      
    case 'Twice a Week':
      if (job.preferredDays?.includes(dayOfWeek)) return 100; // Preferred day
      // For twice a week, other days are still quite viable
      return 60; // Non-preferred day but still good
      
    case 'Every Other Day (M-W-F)':
      if (['Monday', 'Wednesday', 'Friday'].includes(dayOfWeek)) return 100;
      // Tuesday/Thursday could work in a pinch
      if (['Tuesday', 'Thursday'].includes(dayOfWeek)) return 40;
      return 10; // Weekend - not ideal
      
    case 'Monthly':
    case 'Quarterly':
    case 'Semi-Annually':
    case 'Annually':
    case 'On-Demand':
      // These are very flexible
      if (job.preferredDay && dayOfWeek === job.preferredDay) return 95;
      return 85; // High score for any day since these are flexible
      
    default:
      return 50; // Unknown frequency - moderate flexibility
  }
};

/**
 * Calculate workload score - penalize crews that are already busy
 */
const calculateWorkloadScore = (crew, date, weeklyHours, jobHours) => {
  const dayOfWeek = moment(date).format('dddd');
  
  // If crew has no entry in weeklyHours at all, they're completely free
  if (!weeklyHours[crew._id]) {
    const totalWithNewJob = 0 + (jobHours || 0);
    // Give them excellent scores as they're fully available
    if (totalWithNewJob <= 6) return 100; // Perfect - empty crew getting work
    if (totalWithNewJob <= 8) return 95;  // Still great
    return 90; // Good even if job is long
  }
  
  const currentDayHours = weeklyHours[crew._id][dayOfWeek] || { serviceTime: 0, driveTime: 0 };
  const totalCurrentHours = currentDayHours.serviceTime + currentDayHours.driveTime;
  const totalWithNewJob = totalCurrentHours + (jobHours || 0);
  
  // Ideal range is 6-8 hours per day
  if (totalWithNewJob <= 6) return 100; // Under-utilized
  if (totalWithNewJob <= 8) return 90;  // Perfect range
  if (totalWithNewJob <= 9) return 50;  // Slightly over
  if (totalWithNewJob <= 10) return 20; // Overtime territory
  return 0; // Too much work
};

/**
 * Calculate proximity score based on how close the job is to crew's other jobs
 */
const calculateProximityScore = (job, crew, date, weeklyRoutes, sites) => {
  const dayOfWeek = moment(date).format('dddd');
  const routesForDay = weeklyRoutes.find(route => route.day === dayOfWeek);
  
  if (!routesForDay) return 60; // No routes for this day yet
  
  const crewRoute = routesForDay.routes.find(route => route.crewId === crew._id);
  if (!crewRoute || !crewRoute.stops.length) return 70; // First job of the day
  
  // Find average distance to existing stops
  let totalDistance = 0;
  let validDistances = 0;
  
  crewRoute.stops.forEach(stop => {
    const existingSite = sites.find(site => site._id === stop.siteId);
    if (existingSite) {
      const distance = calculateDistance(job, existingSite);
      if (distance > 0) {
        totalDistance += distance;
        validDistances++;
      }
    }
  });
  
  if (validDistances === 0) return 50; // Can't calculate
  
  const avgDistance = totalDistance / validDistances;
  
  // Score based on average distance (closer is better)
  if (avgDistance <= 5) return 100;  // Very close
  if (avgDistance <= 10) return 80;  // Close
  if (avgDistance <= 20) return 60;  // Moderate
  if (avgDistance <= 30) return 40;  // Far
  return 20; // Very far
};

/**
 * Calculate route efficiency score
 */
const calculateRouteEfficiencyScore = (job, crew, date, weeklyRoutes) => {
  const dayOfWeek = moment(date).format('dddd');
  const routesForDay = weeklyRoutes.find(route => route.day === dayOfWeek);
  
  if (!routesForDay) return 80; // New route, decent score
  
  const crewRoute = routesForDay.routes.find(route => route.crewId === crew._id);
  if (!crewRoute) return 80; // No existing route
  
  const currentStops = crewRoute.stops.length;
  const currentMiles = crewRoute.totalMilesDriven || 0;
  
  // Prefer crews with existing routes (economies of scale)
  if (currentStops === 0) return 60; // First stop
  if (currentStops <= 3) return 90;  // Good to add more
  if (currentStops <= 6) return 80;  // Still reasonable
  if (currentStops <= 8) return 60;  // Getting busy
  return 40; // Too many stops already
};

/**
 * Calculate specialization score based on crew experience with similar jobs
 */
const calculateSpecializationScore = (job, crew) => {
  // This would need more data about crew capabilities/experience
  // For now, return a baseline score
  const jobType = job.customerType || 'standard';
  
  // Could be enhanced with crew specialization data
  switch (jobType) {
    case 'Commercial':
      return 80; // Most crews can handle
    case 'Residential':
      return 90; // Standard work
    case 'Industrial':
      return 70; // Might need special equipment
    default:
      return 75; // Default score
  }
};

/**
 * Calculate week balance score for recurring jobs
 */
const calculateWeekBalanceScore = (job, crew, date, weeklyHours) => {
  const weeklyTotal = weeklyHours[crew._id]?.total || { serviceTime: 0, driveTime: 0 };
  const currentWeekHours = weeklyTotal.serviceTime + weeklyTotal.driveTime;
  const newWeekHours = currentWeekHours + (job.estimatedHours || 0);
  
  // Prefer crews with less weekly workload for recurring jobs
  if (['Weekly', 'Bi-Weekly', 'Twice a Week'].includes(job.frequency)) {
    if (newWeekHours <= 30) return 100; // Light week
    if (newWeekHours <= 35) return 80;  // Moderate week
    if (newWeekHours <= 40) return 60;  // Full week
    if (newWeekHours <= 45) return 30;  // Heavy week
    return 10; // Overloaded
  }
  
  return 75; // Not a recurring job, moderate score
};

/**
 * Calculate overtime avoidance score
 */
const calculateOvertimeScore = (crew, date, weeklyHours, jobHours) => {
  const dayOfWeek = moment(date).format('dddd');
  const currentDayHours = weeklyHours[crew._id]?.[dayOfWeek] || { serviceTime: 0, driveTime: 0 };
  const totalCurrentHours = currentDayHours.serviceTime + currentDayHours.driveTime;
  const totalWithNewJob = totalCurrentHours + (jobHours || 0);
  
  // Strongly prefer assignments that don't cause overtime
  if (totalWithNewJob <= 8) return 100; // No overtime
  if (totalWithNewJob <= 8.5) return 70; // Minor overtime
  if (totalWithNewJob <= 9) return 40;  // Moderate overtime
  if (totalWithNewJob <= 10) return 20; // Significant overtime
  return 0; // Excessive overtime
};

/**
 * Get current hours for a crew on a specific day
 */
const getCurrentDayHours = (crew, date, weeklyHours) => {
  const dayOfWeek = moment(date).format('dddd');
  if (!weeklyHours[crew._id]) return 0;
  
  const currentDayHours = weeklyHours[crew._id][dayOfWeek] || { serviceTime: 0, driveTime: 0 };
  return Math.round((currentDayHours.serviceTime + currentDayHours.driveTime) * 100) / 100;
};

/**
 * Calculate estimated total hours for the day with the new job
 */
const calculateEstimatedDayTotal = (crew, date, weeklyHours, jobHours) => {
  const dayOfWeek = moment(date).format('dddd');
  const currentDayHours = weeklyHours[crew._id]?.[dayOfWeek] || { serviceTime: 0, driveTime: 0 };
  const totalCurrentHours = currentDayHours.serviceTime + currentDayHours.driveTime;
  return Math.round((totalCurrentHours + (jobHours || 0)) * 100) / 100;
};

/**
 * Generate human-readable reasoning for the suggestion
 */
const generateReasoningText = (factors, crew, date, job) => {
  const reasons = [];
  const dayOfWeek = moment(date).format('dddd');
  
  // Frequency matching
  if (factors.frequencyScore >= 95) {
    reasons.push("Perfect frequency match");
  } else if (factors.frequencyScore >= 70) {
    reasons.push("Good frequency compatibility");
  } else if (factors.frequencyScore >= 50) {
    reasons.push("Flexible scheduling");
  } else if (factors.frequencyScore >= 30) {
    reasons.push(`⚠️ Non-ideal day (prefers ${job.preferredDay || 'other day'})`);
  } else {
    reasons.push("⚠️ Off-schedule assignment");
  }
  
  // Workload
  if (factors.workloadScore >= 90) {
    reasons.push("Optimal workload");
  } else if (factors.workloadScore >= 60) {
    reasons.push("Manageable workload");
  } else if (factors.workloadScore <= 30) {
    reasons.push("⚠️ Heavy workload");
  }
  
  // Proximity
  if (factors.proximityScore >= 80) {
    reasons.push("Close to existing jobs");
  } else if (factors.proximityScore <= 40) {
    reasons.push("⚠️ Distant from other jobs");
  }
  
  // Overtime
  if (factors.overtimeScore <= 40) {
    reasons.push("⚠️ May cause overtime");
  }
  
  return reasons.join(", ");
};

/**
 * Calculate distance between two job sites (simplified)
 */
const calculateDistance = (job1, job2) => {
  if (!job1 || !job2) return 0;
  
  const lat1 = job1.latitude || job1.location?.coordinates?.[1];
  const lng1 = job1.longitude || job1.location?.coordinates?.[0];
  const lat2 = job2.latitude || job2.location?.coordinates?.[1];
  const lng2 = job2.longitude || job2.location?.coordinates?.[0];
  
  if (!lat1 || !lng1 || !lat2 || !lng2) return 0;
  
  // Haversine formula
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Get top N suggestions with detailed breakdown
 */
export const getTopSuggestions = (suggestions, count = 5) => {
  return suggestions.slice(0, count).map(suggestion => ({
    ...suggestion,
    rank: suggestions.indexOf(suggestion) + 1,
    confidence: calculateConfidence(suggestion.score),
    dateFormatted: moment(suggestion.date).format('dddd, MMM DD'),
    summary: `${suggestion.crew.name} on ${moment(suggestion.date).format('dddd')} (${suggestion.estimatedTotalHours}h total)`
  }));
};

/**
 * Calculate confidence level based on score
 */
const calculateConfidence = (score) => {
  if (score >= 80) return 'High';
  if (score >= 60) return 'Medium';
  if (score >= 40) return 'Low';
  return 'Very Low';
};
